import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import type { OperatorMaterialContext } from "../../../shared/production/operator-materials";
import { materialScrapEntrySchema, type MaterialScrapEntry, type MaterialScrapWrite } from "../../../shared/production/material-scrap";
import { validQuantityPrecision } from "../../../shared/production/quantity";
import type { ProductionRun } from "./models";
import { productionGateway } from "./production-gateway";
import { loadCriticalWrite, removeCriticalWrite, saveCriticalWrite } from "./critical-write-store";
import { ApiError } from "../../shared/api/client";
import { criticalWriteFailureAction, type CriticalWriteDeliveryState } from "./critical-write-failure";

export type MaterialScrapDraft = {
  entryType: "WASTE" | "SCRAP";
  materialCode: string;
  quantity: string;
  unit: string;
  reasonCodeId: string;
  note: string;
  occurredAt: string;
};

type SupervisorApproval = { supervisorId: string; pin: string };
type PendingWrite = {
  write: Omit<MaterialScrapWrite, "approvalToken">;
  expectedSessionId: string;
  deliveryState: CriticalWriteDeliveryState;
  createdAt: string;
};
const receiptSchema = z.object({ status: z.enum(["CONFIRMED", "NOT_FOUND"]), entry: materialScrapEntrySchema.nullable() }).strict();
const stalePayloadCodes = new Set(["PRODUCTION_RUN_VERSION_CHANGED", "PRODUCTION_MATERIAL_CONTEXT_CHANGED", "PRODUCTION_MATERIAL_INVALID"]);

function actualOccurrence(value: string, historical: boolean): string {
  if (!value && historical) throw new Error("Iskartanın gerçek oluşma zamanını girin.");
  const local = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?$/u.test(value);
  const parsed = value ? new Date(local ? `${value}${value.length === 16 ? ":00" : ""}+03:00` : value) : new Date();
  if (!Number.isFinite(parsed.getTime())) throw new Error("Gerçek oluşma zamanı geçerli değil.");
  return parsed.toISOString();
}

export function useMaterialScrapWorkspace({ run, terminalCode, sessionId, operatorId, preview, onSaved }: {
  run: ProductionRun; terminalCode: string; sessionId: string; operatorId: string; preview: boolean; onSaved: () => void;
}) {
  const [context, setContext] = useState<OperatorMaterialContext | null>(null);
  const [history, setHistory] = useState<MaterialScrapEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingWrite | null>(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const refresh = useCallback(async () => {
    const controller = new AbortController();
    setLoading(true); setError(null);
    try {
      const [materials, entries] = await Promise.all([
        productionGateway.operatorMaterials(run.id, terminalCode, controller.signal),
        productionGateway.materialScrapHistory(run.id, terminalCode, controller.signal),
      ]);
      if (!mounted.current) return;
      setContext(materials); setHistory(entries);
    } catch (caught) {
      if (mounted.current) setError(caught instanceof Error ? caught.message : "Malzeme ıskarta bilgileri yüklenemedi.");
    } finally { if (mounted.current) setLoading(false); }
  }, [run.id, terminalCode]);
  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    let active=true;
    void loadCriticalWrite(preview,{terminalCode,operatorId,sessionId,runId:run.id},"MATERIAL_SCRAP").then((stored)=>{
      if (active && stored?.kind === "MATERIAL_SCRAP") {
        const { expectedSessionId, ...write } = stored.payload;
        const deliveryState = stored.deliveryState === "SENDING" ? "OUTCOME_UNKNOWN" : stored.deliveryState;
        const restored = { write, expectedSessionId, deliveryState, createdAt: stored.createdAt };
        setPending(restored);
        if (deliveryState !== stored.deliveryState) {
          void saveCriticalWrite(preview, { ...stored, deliveryState }).catch((caught: unknown) => {
            if (active) setError(caught instanceof Error ? caught.message : "Bekleyen kayıt durumu cihazda güncellenemedi.");
          });
        }
      }
    }).catch((caught:unknown)=>{if(active)setError(caught instanceof Error?caught.message:"Bekleyen kritik kayıt okunamadı.");});
    return()=>{active=false;};
  },[operatorId,preview,run.id,sessionId,terminalCode]);

  const persistIntent = useCallback((intent: PendingWrite, deliveryState: CriticalWriteDeliveryState) =>
    saveCriticalWrite(preview, { kind: "MATERIAL_SCRAP", idempotencyKey: intent.write.idempotencyKey,
      terminalCode, operatorId, sessionId: intent.expectedSessionId, runId: run.id, createdAt: intent.createdAt,
      deliveryState, payload: { ...intent.write, expectedSessionId: intent.expectedSessionId } }),
  [operatorId, preview, run.id, terminalCode]);

  const send = useCallback(async (intent: PendingWrite, approvalToken: string | null) => {
    const controller = new AbortController();
    setSaving(true); setError(null);
    const stateBeforeAttempt = intent.deliveryState;
    const sending = { ...intent, deliveryState: "SENDING" as const };
    try {
      await persistIntent(sending, "SENDING");
      if (mounted.current) setPending(sending);
    } catch (caught) {
      if (mounted.current) {
        setSaving(false);
        setError(caught instanceof Error ? caught.message : "Kritik kayıt cihazda gönderime hazırlanamadı.");
      }
      throw caught;
    }
    let entry: MaterialScrapEntry;
    try {
      entry = await productionGateway.materialScrap(run.id, { ...intent.write, approvalToken,
        expectedSessionId: intent.expectedSessionId }, terminalCode, controller.signal);
    } catch (caught) {
      try {
        if (criticalWriteFailureAction(stateBeforeAttempt, caught) === "REMOVE") {
          await removeCriticalWrite(preview, intent.write.idempotencyKey);
          if (mounted.current) setPending(null);
        } else {
          const uncertain = { ...intent, deliveryState: "OUTCOME_UNKNOWN" as const };
          await persistIntent(uncertain, "OUTCOME_UNKNOWN");
          if (mounted.current) setPending(uncertain);
        }
      } finally {
        if (mounted.current) setSaving(false);
      }
      if (mounted.current) setError(caught instanceof Error ? caught.message : "Kayıt sonucu doğrulanamadı. Aynı işlem kimliği korunuyor.");
      throw caught;
    }
    const confirmed = { ...intent, deliveryState: "REMOTE_CONFIRMED" as const };
    try {
      await persistIntent(confirmed, "REMOTE_CONFIRMED");
      if (mounted.current) setPending(confirmed);
      await removeCriticalWrite(preview, intent.write.idempotencyKey);
      if (mounted.current) {
        setPending(null);
        setHistory((current) => [entry, ...current.filter((item) => item.id !== entry.id)]);
        onSaved();
      }
      return entry;
    } catch (caught) {
      if (mounted.current) {
        setPending(confirmed);
        setError("Kayıt sunucuda doğrulandı; cihazdaki bekleyen işaret temizlenemedi. Sonucu tekrar kontrol edin.");
      }
      throw caught;
    } finally { if (mounted.current) setSaving(false); }
  }, [onSaved, persistIntent, preview, run.id, terminalCode]);

  const releasePreparedStaleApproval = useCallback(async (intent: PendingWrite, caught: unknown) => {
    if (intent.deliveryState === "PREPARED" && caught instanceof ApiError && stalePayloadCodes.has(caught.code)) {
      await removeCriticalWrite(preview, intent.write.idempotencyKey);
      setPending(null);
    }
  }, [preview]);

  const submit = useCallback(async (draft: MaterialScrapDraft, supervisor?: SupervisorApproval) => {
    if (!context?.available || !context.recipeVersion || context.runVersion !== run.version) throw new Error("Reçete görünümü güncel değil. Malzemeleri yenileyin.");
    const material = context.materials.find((item) => item.materialCode === draft.materialCode && item.unit === draft.unit);
    if (!material || material.dataIssues.length) throw new Error("Malzemenin reçete ve birim bilgisi doğrulanamadı.");
    const quantity = Number(draft.quantity.replace(",", "."));
    if (!validQuantityPrecision(quantity)) throw new Error("Geçerli bir miktar girin.");
    if (pending) throw new Error("Önce bekleyen malzeme ıskarta kaydının sonucunu doğrulayın.");
    const occurredAt = actualOccurrence(draft.occurredAt, run.status === "COMPLETED");
    const base: Omit<MaterialScrapWrite,"approvalToken"> = {
      entryType: draft.entryType, materialCode: material.materialCode, quantity, unit: material.unit,
      reasonCodeId: draft.reasonCodeId, note: draft.note.trim() || null, occurredAt,
      idempotencyKey: crypto.randomUUID(), expectedRunVersion: run.version, recipeVersion: context.recipeVersion,
    };
    const createdAt = new Date().toISOString();
    const intent: PendingWrite = { write: base, expectedSessionId: sessionId, deliveryState: "PREPARED", createdAt };
    await saveCriticalWrite(preview,{kind:"MATERIAL_SCRAP",idempotencyKey:base.idempotencyKey,terminalCode,operatorId,sessionId,runId:run.id,
      createdAt,deliveryState:"PREPARED",payload:{...base,expectedSessionId:sessionId}});
    setPending(intent);
    let approvalToken: string | null = null;
    if (run.status === "COMPLETED") {
      if (!supervisor || !/^\d{4}$/u.test(supervisor.pin)) throw new Error("Vardiya amirini seçin ve dört haneli PIN’i girin.");
      try {
        const approval = await productionGateway.approveTerminalAction(terminalCode, {
          ...base, supervisorId: supervisor.supervisorId, pin: supervisor.pin,
          actionScope: "POST_COMPLETION_MATERIAL_SCRAP", targetRef: run.id,
        });
        approvalToken = approval.approvalToken;
      } catch (caught) {
        await releasePreparedStaleApproval(intent, caught);
        throw caught;
      }
    }
    return send(intent,approvalToken);
  }, [context, operatorId, pending, preview, releasePreparedStaleApproval, run.id, run.status, run.version, send, sessionId, terminalCode]);

  const verifyPending = useCallback(async () => {
    if (!pending) return null;
    setSaving(true); setError(null);
    try {
      const result = receiptSchema.parse(await productionGateway.materialScrapReceipt(run.id, pending.write.idempotencyKey,
        pending.expectedSessionId, terminalCode));
      if (result.status === "CONFIRMED" && result.entry && result.entry.enteredBy === operatorId
        && result.entry.operatorSessionId === pending.expectedSessionId) {
        const confirmed = result.entry;
        if (mounted.current) {
          await removeCriticalWrite(preview,pending.write.idempotencyKey);
          setPending(null); setHistory((current) => [confirmed, ...current.filter((item) => item.id !== confirmed.id)]); onSaved();
        }
        return confirmed;
      }
      setError("Sunucuda henüz kayıt bulunamadı. Aynı kayıt kimliği korunuyor; tamamlanmış işte yeniden amir onayı alınabilir.");
      return null;
    } catch (caught) {
      if (mounted.current) setError(caught instanceof Error ? caught.message : "Kayıt sonucu doğrulanamadı.");
      return null;
    } finally { if (mounted.current) setSaving(false); }
  }, [onSaved, operatorId, pending, preview, run.id, terminalCode]);

  const reauthorizePending = useCallback(async(supervisor:SupervisorApproval)=>{
    if(!pending) return null;
    if(run.status!=="COMPLETED") return send(pending,null);
    if(!/^\d{4}$/u.test(supervisor.pin)) throw new Error("Vardiya amirinin dört haneli PIN’ini girin.");
    try {
      const approval=await productionGateway.approveTerminalAction(terminalCode,{...pending.write,supervisorId:supervisor.supervisorId,pin:supervisor.pin,
        actionScope:"POST_COMPLETION_MATERIAL_SCRAP",targetRef:run.id});
      return send(pending,approval.approvalToken);
    } catch(caught) {
      await releasePreparedStaleApproval(pending,caught);
      throw caught;
    }
  },[pending,releasePreparedStaleApproval,run.id,run.status,send,terminalCode]);

  return { context, history, loading, saving, error, pending, refresh, submit, verifyPending, reauthorizePending };
}
